import { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { useSiteContent } from '../context/SiteContentContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { siteContent } = useSiteContent() || {};
  const phone = siteContent?.contact?.phone || '+91 96496 11111';
  const phoneTel = phone.replace(/\s/g, '');
  const email = siteContent?.contact?.email || 'info@rudragarh.com';
  const address = siteContent?.contact?.address || 'Rudragarh Resort, Village Damodra, Sam Road, Jaisalmer, Rajasthan 345001';
  const mapEmbed = siteContent?.contact?.map_embed || 'https://maps.google.com/maps?q=Rudragarh+Resort+%26+Hotel+Damodra+Jaisalmer&z=13&output=embed';
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully! We will get back to you soon.');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20">
      <Helmet>
        <title>Contact Us | Rudragarh Resort, Jaisalmer</title>
        <meta name="description" content="Get in touch with Rudragarh Resort near Jaisalmer, Rajasthan. Call, email, or send us a message — we'd love to help plan your desert stay." />
        <meta property="og:title" content="Contact | Rudragarh Resort" />
        <meta property="og:description" content="Reach out to Rudragarh Resort near Jaisalmer for bookings, enquiries, and travel planning." />
        <meta property="og:url" content="https://www.rudragarh.com/contact" />
        <meta property="og:image" content="https://www.rudragarh.com/images/resort-pool-night.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact | Rudragarh Resort" />
        <meta name="twitter:description" content="Reach out to Rudragarh Resort near Jaisalmer for bookings, enquiries, and travel planning." />
        <meta name="twitter:image" content="https://www.rudragarh.com/images/resort-pool-night.jpg" />
        <link rel="canonical" href="https://www.rudragarh.com/contact" />
      </Helmet>
      {/* Hero */}
      <section className="relative h-[40vh] flex items-center justify-center" data-testid="contact-hero">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('/images/resort-huts-dusk.jpg')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-void/70 via-void/50 to-void" />
        <div className="relative z-10 text-center px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="overline">Get in Touch</span>
            <h1 className="text-4xl md:text-6xl font-heading font-medium mb-4">
              <span className="text-gold-500">Contact</span> Us
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-padding" data-testid="contact-section">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div>
                <h2 className="text-3xl font-heading font-medium mb-4">
                  Visit <span className="text-gold-500">Rudragarh</span>
                </h2>
                <p className="text-foreground/70 leading-relaxed">
                  We'd love to hear from you. Whether you have a question about 
                  accommodations, experiences, or anything else, our team is ready to help.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-gold-500/10 text-gold-500">
                    <MapPin size={24} />
                  </div>
                  <div>
                    <h4 className="font-heading text-lg mb-1">Location</h4>
                    <p className="text-foreground/60">{address}</p>
                    <p className="text-foreground/50 text-sm mt-1">21 km from Jaisalmer railway station &amp; airport</p>
                    <a
                      href="https://share.google/JDO0fSPxDmYuYY1vX"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gold-500 text-sm hover:underline"
                      data-testid="get-directions-link"
                    >
                      Get Directions →
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 bg-gold-500/10 text-gold-500">
                    <Phone size={24} />
                  </div>
                  <div>
                    <h4 className="font-heading text-lg mb-1">Phone</h4>
                    <a href={`tel:${phoneTel}`} className="text-foreground/60 hover:text-gold-500 transition-colors">
                      {phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 bg-gold-500/10 text-gold-500">
                    <Mail size={24} />
                  </div>
                  <div>
                    <h4 className="font-heading text-lg mb-1">Email</h4>
                    <a href={`mailto:${email}`} className="text-foreground/60 hover:text-gold-500 transition-colors">
                      {email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 bg-gold-500/10 text-gold-500">
                    <Clock size={24} />
                  </div>
                  <div>
                    <h4 className="font-heading text-lg mb-1">Reception Hours</h4>
                    <p className="text-foreground/60">
                      24/7 - We're always here for you
                    </p>
                  </div>
                </div>
              </div>

              {/* Google Maps */}
              <div className="h-64 border border-gold-500/10 overflow-hidden">
                <iframe
                  src={mapEmbed}
                  width="100%"
                  height="100%"
                  style={{ border: 0, filter: 'grayscale(30%) contrast(1.1)' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Rudragarh Resort Location — Damodra, Sam Road, Jaisalmer"
                />
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <form onSubmit={handleSubmit} className="card-glass p-8 space-y-6" data-testid="contact-form">
                <h3 className="font-heading text-2xl text-gold-500 mb-2">Send us a Message</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-sm text-foreground/60 mb-2 block">Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full input-default px-4"
                      placeholder="Your name"
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-foreground/60 mb-2 block">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full input-default px-4"
                      placeholder="your@email.com"
                      data-testid="contact-email"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-sm text-foreground/60 mb-2 block">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full input-default px-4"
                      placeholder="+91 XXXXX XXXXX"
                      data-testid="contact-phone"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-foreground/60 mb-2 block">Subject *</label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full input-default px-4"
                      placeholder="How can we help?"
                      data-testid="contact-subject"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm text-foreground/60 mb-2 block">Message *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={5}
                    className="w-full input-default px-4 py-3 resize-none"
                    placeholder="Tell us about your inquiry..."
                    data-testid="contact-message"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="contact-submit"
                >
                  {submitting ? (
                    'Sending...'
                  ) : (
                    <>
                      <Send size={18} />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
